import { Component } from "@theme/component";
import { isDesktopBreakpoint, isMobileBreakpoint, positionHotspotTooltip, scrollHorizontalToElement } from "@theme/utilities";

class ShopTheLookComponent extends Component {
  // Cached elements
  #productCarousel = null;
  #productCards = null;
  #hotspotsArray = null;
  #scrollContainer = null;
  #indexActiveHotspot = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#cacheElements();
    this.#setupHotspots();
    this.#hoverProductCard();
    this.#changeSwiperSlide();

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (e) => {
        this.#cacheElements();
        this.#changeSwiperSlide();
      });
    }
  }

  /**
   * Caches frequently accessed DOM elements
   */
  #cacheElements() {
    this.#productCarousel = this.querySelector("carousel-component");
    this.#productCards = Array.from(this.querySelectorAll(".look-product-card"));
    this.#scrollContainer = this.querySelector(".swipe-mobile");
  }

  /**
   * Collects each card's hotspot, moves them onto the image and wires their events
   */
  #setupHotspots() {
    const image = this.querySelector(".shop-the-look__image");
    this.#hotspotsArray = [];

    this.#productCards.forEach((productCard) => {
      const hotspot = productCard.querySelector(".shop-the-look__hotspot");
      if (!hotspot) return;

      hotspot.classList.remove("hidden");
      image.appendChild(hotspot);
      this.#attachHotspotEvents(hotspot);
      this.#hotspotsArray.push(hotspot);
    });

    this.#setActiveHotspot(this.#hotspotsArray[0]);
  }

  /**
   * Handles hover event for product cards
   */
  #hoverProductCard() {
    const { signal } = this.abortController;

    this.#productCards.forEach((productCard) => {
      productCard.addEventListener('mouseenter', () => {
        const currentIndex = this.#productCards.indexOf(productCard);
        const hotspot = this.#hotspotsArray[currentIndex];
        if (hotspot) {
          this.#setActiveHotspot(hotspot);
        }
      }, { signal });

      productCard.addEventListener('mouseleave', () => {
        const hotspot = this.#hotspotsArray[this.#indexActiveHotspot];
        if (hotspot) {
          this.#setActiveHotspot(hotspot);
        }
      }, { signal });
    });
  }

  /**
   * Changes the index active hotspot when the swiper slide changes
   */
  #changeSwiperSlide() {
    if (!this.#productCarousel) return;
    const { signal } = this.abortController;
    const swiper = this.#productCarousel.swiper;
    if (!swiper) return;
    swiper.on('slideChange', () => {
      this.#indexActiveHotspot = swiper.activeIndex;
      const hotspot = this.#hotspotsArray[this.#indexActiveHotspot];
      if (hotspot) {
        this.#setActiveHotspot(hotspot);
      }
    }, { signal });
  }

  /**
   * Attaches event listeners to hotspot
   * @param {HTMLElement} hotspotEl - The hotspot element
   */
  #attachHotspotEvents(hotspotEl) {
    if (this.#productCarousel) {
      hotspotEl.addEventListener("pointerenter", this.#handleHotspotPointerEnter);
      hotspotEl.addEventListener("pointerleave", this.#handleHotspotPointerLeave);
    }

    hotspotEl.addEventListener("click", this.#handleHotspotClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Cleanup hotspot events
    if (this.#hotspotsArray) {
      this.#hotspotsArray.forEach(hotspot => {
        hotspot.removeEventListener("pointerenter", this.#handleHotspotPointerEnter);
        hotspot.removeEventListener("pointerleave", this.#handleHotspotPointerLeave);
        hotspot.removeEventListener("click", this.#handleHotspotClick);
      });
    }
  }

  /**
   * Sets the active hotspot and removes active state from others
   * @param {HTMLElement} hotspotEl - The hotspot element to activate
   */
  #setActiveHotspot(hotspotEl) {
    if (!this.#hotspotsArray || !hotspotEl) return;

    this.#hotspotsArray.forEach((el) => el.classList.remove("is-active"));
    hotspotEl.classList.add("is-active");
  }

  /**
   * Handles pointer enter event (hover) for hotspots
   * Only triggers on desktop with mouse pointer when carousel is present
   */
  #handleHotspotPointerEnter = (event) => {
    if (!this.#productCarousel || !isDesktopBreakpoint() || event.pointerType !== 'mouse') {
      return;
    }

    const hotspot = event.currentTarget ?? event.target;
    if (!hotspot) return;

    this.classList.add("is-hovering");
    this.#productCarousel.holdAutoplay(true);
    this.#showTooltip(hotspot);
    this.#activateHotspotAndScroll(hotspot);
  };

  /**
   * Handles pointer leave event (hover out) for hotspots
   * Chrome re-fires the leave with the hotspot itself as the related target while the pointer is
   * still inside it, so a leave that lands back on the hotspot is ignored rather than blinking the
   * tooltip shut mid-fade
   */
  #handleHotspotPointerLeave = (event) => {
    const hotspot = event.currentTarget ?? event.target;
    if (hotspot?.contains(event.relatedTarget)) return;

    this.classList.remove("is-hovering");
    hotspot?.classList.remove("is-tooltip-open");

    if (event.relatedTarget?.closest?.(".shop-the-look__hotspot")) return;

    this.#productCarousel?.holdAutoplay(false);
  };

  /**
   * Opens the hovered hotspot's product tooltip, kept inside the image the hotspots sit on so a
   * rounded image can't clip it
   * @param {HTMLElement} hotspotEl - The hovered hotspot element
   */
  #showTooltip(hotspotEl) {
    const image = this.querySelector(".shop-the-look__image");
    if (!image) return;

    positionHotspotTooltip(hotspotEl, image);
    hotspotEl.classList.add("is-tooltip-open");
  }

  /**
   * Handles click event for hotspots
   * Works for both mouse clicks and touch taps
   */
  #handleHotspotClick = (event) => {
    const hotspot = event.currentTarget ?? event.target;
    if (!hotspot) return;

    this.#activateHotspotAndScroll(hotspot);
  };

  /**
   * Activates hotspot and scrolls to corresponding product
   * @param {HTMLElement} hotspotEl - The hotspot element
   */
  #activateHotspotAndScroll(hotspotEl) {
    this.#setActiveHotspot(hotspotEl);
    this.#scrollToProduct(hotspotEl);
    this.#indexActiveHotspot = this.#hotspotsArray.indexOf(hotspotEl);
  }

  /**
   * Scrolls to the product corresponding to the hotspot
   * @param {HTMLElement} hotspotEl - The hotspot element
   */
  #scrollToProduct(hotspotEl) {
    if (!this.#hotspotsArray) return;
    const index = this.#hotspotsArray.indexOf(hotspotEl);
    if (index < 0) return;

    const targetCard = this.#productCards[index];
    if (!targetCard) return;

    // Desktop carousel: use carousel navigation
    if (this.#productCarousel && !isMobileBreakpoint()) {
      this.#scrollCarouselToIndex(index, targetCard);
      return;
    }

    // Mobile grid: use horizontal scroll
    if (isMobileBreakpoint()) {
      scrollHorizontalToElement(this.#scrollContainer, targetCard);
    }

    // Fallback: use native scrollIntoView
    targetCard.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }

  /**
   * Scrolls carousel to specific index and marks card as active
   * @param {number} index - The target index
   * @param {HTMLElement} targetCard - The target card element
   */
  #scrollCarouselToIndex(index, targetCard) {
    this.#productCards.forEach((card) => card.classList.remove("is-active"));
    targetCard.classList.add("is-active");
    this.#productCarousel.slideTo(index);
  }

}

if (!customElements.get("shop-the-look")) {
  customElements.define("shop-the-look", ShopTheLookComponent);
}
